import { Injectable } from '@angular/core';
import { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import { DirectoryHistoryServiceInterface } from '@zaoblako/shared/common/interfaces/directory-history-service.interface';
import { DirectionAddUserFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-add-user-form.dto';
import { DirectionAddUserGroupFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-add-user-group-form.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { DirectionGroupFormInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-group-form.dto';
import type { DirectionQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-query.dto';
import type { DirectionsReorderInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/directions-reorder.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DirectionService extends BaseService implements DirectoryHistoryServiceInterface {
  protected baseUrl = '/basis/catalog/direction';

  public flat(queryParams?: DirectionQueryInterface): Observable<DirectionFindInterface[]> {
    const params = queryParams ? this.objectToHttpParams(queryParams) : {};
    return this.http.get<DirectionFindInterface[]>(`${this.baseUrl}/flat`, { params });
  }

  public history(queryParams: PaginationRequiredQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public createGroup(directionGroup: DirectionGroupFormInterface): Observable<DirectionFindInterface> {
    return this.http.post<DirectionFindInterface>(`${this.baseUrl}/group`, directionGroup);
  }

  public synchronize1cAll(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/synchronize-1c`);
  }

  public synchronizeAtriumAll(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/synchronize-atrium`);
  }

  public restore(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/restore`, {});
  }

  public getLeafDirections<T extends { children?: any[] }>(directions: T[]): T[] {
    let leafDirections = [];

    directions?.forEach((direction) => {
      if (direction.children?.length) {
        leafDirections.push(...this.getLeafDirections(direction.children));
      } else {
        leafDirections.push(direction);
      }
    });

    return [...new Set(leafDirections)];
  }

  public getLeafDirectionsWithParent<T extends { children?: any[] }>(directions: T[]): T[] {
    let leafDirections = [];

    const parseDirections = (_directions: T[]) => {
      _directions?.forEach((direction) => {
        leafDirections.push(direction);
        if (direction.children?.length) {
          parseDirections(direction.children);
        }
      });
    };

    parseDirections(directions);
    return [...new Set(leafDirections)];
  }

  public getRootDirections<T extends { id: number; parent?: T }>(directions: T[]): T[] {
    const rootDirections = (directions || [])?.filter(
      (direction) => directions?.findIndex((_direction) => _direction.id === direction.parent?.id) === -1
    );
    return [...new Set(rootDirections)];
  }

  public getHistoryForOne(id: number, queryParams?: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public addUser(id: number, body: DirectionAddUserFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/add-user`, body);
  }

  public addUserGroup(id: number, body: DirectionAddUserGroupFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/add-user-group`, body);
  }

  public reorderDirections(body: DirectionsReorderInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/reorder`, body);
  }
}
